/**
 * WorkflowCanvas - Mapa do processo na notação BPMN (fluxo vertical)
 *
 * Desenha o grafo vindo de `get_workflow_editor` com símbolos BPMN (eventos,
 * tarefas, gateways) organizados de cima para baixo. Cada linha possui um
 * botão "+" que insere um bloco no meio do caminho, e blocos sem saída exibem
 * um "+" para continuar o ramo. Camada de apresentação apenas.
 *
 * @version 3.4.0
 * RELEASE: 2026-09-04 v3.4.0 - Layout unificado: um único cartão (`BpmnNodeCard`)
 *          para todos os blocos, mantendo Início e Fim como círculos.
 * @version 3.3.0
 * RELEASE: 2026-08-31 v3.3.0 - Barra vertical única (mão/setinha + zoom), mini
 *          mapa à esquerda e tecla Delete excluindo de verdade (RPC).
 * RELEASE: 2026-08-31 v3.2.0 - Ferramenta de seleção múltipla (mão x setinha)
 *          com Shift para somar blocos e arraste do conjunto.
 * RELEASE: 2026-08-31 v3.1.0 - Arraste preservado (o bloco só volta à posição
 *          do servidor quando ela realmente muda) e numeração dos passos.
 * RELEASE: 2026-08-31 v3.0.0 - Remoção das raias por módulo, layout vertical e
 *          inserção guiada de blocos pelas linhas e pelos blocos.
 * RELEASE: 2026-08-31 v2.0.0 - Refatoração para notação BPMN, raias por módulo,
 *          arestas ortogonais e auto-organização horizontal (dagre).
 * RELEASE: 2026-08-30 v1.0.0 - Criação inicial (designer v2)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background,
  MiniMap,
  MarkerType,
  Panel,
  ReactFlow,
  SelectionMode,
  useEdgesState,
  useNodesState,
  useReactFlow,
  ReactFlowProvider,
  type Connection,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Hand, MousePointer2, Plus, Minus, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { BpmnEventNode } from './bpmn/BpmnEventNode';
import { BpmnNodeCard } from './bpmn/BpmnNodeCard';
import { BpmnInsertEdge } from './bpmn/BpmnInsertEdge';
import { formaDoRegistro, tipoDeComponente } from './bpmn/bpmnShapes';
import { calcularLayoutBpmn } from './bpmn/bpmnLayout';
import { describeNodeConfig, type WorkflowPaletteItem } from './workflowNodeMeta';
// v3.5.0: fichas legíveis das regras exibidas dentro dos blocos
import { linhasDoResumo } from './inspector/WorkflowConditionSummary';
// v4.0.0: saídas dos gateways montadas aqui e desenhadas dentro do bloco
import { contarRegras } from './inspector/WorkflowConditionBuilder';
import { ehPlaceholder } from './bpmn/bpmnPlaceholder';
import type { SaidaDoBloco } from './bpmn/BpmnNodeCard';
import type { WorkflowEditorEdge, WorkflowEditorNode, WorkflowCatalogItem } from '@/hooks/useWorkflowDesigner';


/**
 * Códigos publicados pelo motor (`workflow_apply_human_resolution`) quando uma
 * pendência de aprovação é resolvida. São eles que identificam cada desfecho.
 */
export const EVENTO_APROVADO = 'approval.approved';
export const EVENTO_REPROVADO = 'approval.rejected';

const nodeTypes = {
  bpmnEvent: BpmnEventNode,
  bpmnCard: BpmnNodeCard,
};

const edgeTypes = {
  bpmnInsert: BpmnInsertEdge,
};


interface WorkflowCanvasProps {
  nodes: WorkflowEditorNode[];
  edges: WorkflowEditorEdge[];
  actionCatalog: WorkflowCatalogItem[];
  eventCatalog: WorkflowCatalogItem[];
  selectedNodeId?: string | null;
  selectedEdgeId?: string | null;
  /** Habilita os botões "+" (respeita a permissão de edição do módulo) */
  podeEditar?: boolean;
  onSelectNode: (id: string | null) => void;
  onSelectEdge: (id: string | null) => void;
  /** v3.1.0: `handle` indica a âncora usada ("sim" | "nao") em blocos de Condição */
  onConnect: (from: string, to: string, handle?: string | null) => void;

  onMoveEnd: (positions: Array<{ id: string; x: number; y: number }>) => void;
  /** Insere um bloco no meio de uma linha existente */
  onInsertOnEdge: (edgeId: string, item: WorkflowPaletteItem) => void;
  /** Acrescenta um bloco logo após um bloco (handle: âncora usada, quando houver) */
  onAppendAfterNode: (nodeId: string, item: WorkflowPaletteItem, handle?: string | null) => void;
  /** v4.1.0: cria uma ramificação nova com o bloco escolhido na lista ("+" do gateway) */
  onAdicionarSaida?: (nodeId: string, item: WorkflowPaletteItem) => void;
  /** v4.0.0: remove uma saída pelo cartão do gateway */
  onRemoverSaida?: (edgeId: string) => void;

  /** Exclui de verdade um bloco (tecla Delete) */
  onDeleteNode?: (id: string) => void;
  /** Exclui de verdade uma linha (tecla Delete) */
  onDeleteEdge?: (id: string) => void;
  /**
   * v3.4.0: contador incrementado pela página quando o usuário clica em
   * "Organizar". O canvas reposiciona os blocos na hora (sem esperar o
   * servidor) e devolve as novas posições para serem gravadas.
   */
  organizarSinal?: number;
}


export function WorkflowCanvas(props: WorkflowCanvasProps) {
  // O provider é necessário para a barra de zoom usar `useReactFlow`
  return (
    <ReactFlowProvider>
      <CanvasInterno {...props} />
    </ReactFlowProvider>
  );
}

function CanvasInterno({
  nodes,
  edges,
  actionCatalog,
  eventCatalog,
  selectedNodeId,
  selectedEdgeId,
  podeEditar = true,
  onSelectNode,
  onSelectEdge,
  onConnect,
  onMoveEnd,
  onInsertOnEdge,
  onAppendAfterNode,
  onAdicionarSaida,
  onRemoverSaida,
  onDeleteNode,
  onDeleteEdge,
  organizarSinal = 0,
}: WorkflowCanvasProps) {

  // Mapas código -> rótulo, usados nos badges e resumos dos blocos
  const actionLabels = useMemo(
    () => Object.fromEntries(actionCatalog.map((a) => [a.code, a.label])),
    [actionCatalog],
  );
  const eventLabels = useMemo(
    () => Object.fromEntries(eventCatalog.map((a) => [a.code, a.label])),
    [eventCatalog],
  );

  /** Posições calculadas automaticamente para blocos que ainda não têm posição */
  const layoutAutomatico = useMemo(() => {
    const mapa = new Map<string, { x: number; y: number }>();
    calcularLayoutBpmn(nodes, edges).forEach((p) => mapa.set(p.id, { x: p.x, y: p.y }));
    return mapa;
  }, [nodes, edges]);

  /** Converte o payload da RPC em nodes do React Flow com a forma BPMN correta */
  const rfNodes = useMemo<Node[]>(
    () =>
      nodes.map((n, indice) => {
        const forma = formaDoRegistro(n);
        const auto = layoutAutomatico.get(n.id);
        const saidas = edges.filter((e) => e.from_node_id === n.id).length;

        // Resumo em português exibido dentro do bloco
        let resumo = describeNodeConfig(n.node_type, n.config);
        if (n.node_type === 'inicio') {
          const gatilhos: string[] = Array.isArray(n.config?.trigger_events) ? n.config!.trigger_events : [];
          resumo =
            gatilhos.length > 0
              ? gatilhos.map((c) => eventLabels[c] ?? c).join(' · ')
              : 'Gatilho não configurado';
        }
        // v3.4.0: bloco de espera mostra o nome dos eventos aguardados
        if (n.node_type === 'espera' && String(n.config?.node_kind ?? '') !== 'exigencia') {
          const esperados: string[] = Array.isArray(n.config?.event_codes)
            ? (n.config!.event_codes as string[])
            : n.config?.event_code
              ? [String(n.config.event_code)]
              : [];
          if (esperados.length > 0) {
            resumo = `Aguarda: ${esperados.map((c) => eventLabels[c] ?? c).join(' · ')}`;
          }
        }

        // Avisos de desenho incompleto, direto no bloco
        let alerta: string | null = null;
        if (n.node_type !== 'fim' && saidas === 0) alerta = 'Sem saída configurada';
        if (forma === 'gateway_exclusivo' && saidas === 1) alerta = 'Só existe um caminho';

        // v4.0.0: gateways mostram AS SAÍDAS dentro do bloco (uma linha por
        // caminho). A regra de cada caminho vive na própria linha
        // (`workflow_edges.conditions` + `is_default`), que é o que o motor lê.
        // v5.0.0: o bloco no mapa é só o losango. As saídas servem apenas para
        // gerar as âncoras; nome, regra e destino ficam na linha/painel lateral.
        let saidasLista: SaidaDoBloco[] | undefined;
        const ehGateway = forma.startsWith('gateway_');
        if (ehGateway) {
          const nomeDoNode = (alvo: string) => {
            const destino = nodes.find((x) => x.id === alvo);
            return ehPlaceholder(destino) ? null : (destino?.label ?? null);
          };

          if (forma === 'gateway_juncao') {
            const ramos = edges.filter((e) => e.to_node_id === n.id).length;
            if (ramos < 2) alerta = 'Junção com menos de dois ramos';
          } else {
            saidasLista = edges
              .filter((e) => e.from_node_id === n.id)
              .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
              .map((e, i) => ({
                id: e.id,
                rotulo: e.label || (e.is_default ? 'senão' : `Caminho ${i + 1}`),
                destino: nomeDoNode(e.to_node_id),
                padrao: !!e.is_default,
                semRegra: !e.is_default && !e.event_code && contarRegras(e.conditions) === 0,
              }));

            const semPadrao = saidasLista.every((s) => !s.padrao);
            if (saidasLista.length === 0) alerta = 'Nenhuma saída criada';
            else if (forma !== 'gateway_paralelo' && semPadrao)
              alerta = 'Sem caminho padrão: se nenhuma regra passar, o processo para aqui';
            else if (saidasLista.filter((s) => s.padrao).length > 1)
              alerta = 'Existe mais de um caminho padrão';
          }
        }


        // v3.6.0: tarefa humana de APROVAÇÃO tem dois desfechos nomeados.
        // O motor publica `approval.approved` / `approval.rejected`, então é o
        // próprio `event_code` da linha que identifica cada caminho.
        const aprovacao =
          n.node_type === 'tarefa_humana' && String(n.config?.human_kind ?? 'aprovacao') === 'aprovacao';
        let caminhoAprovado: string | null = null;
        let caminhoReprovado: string | null = null;
        if (aprovacao) {
          const nomeDoNode = (id: string) => nodes.find((x) => x.id === id)?.label ?? '—';
          const saidasDoNode = edges.filter((e) => e.from_node_id === n.id);
          const ok = saidasDoNode.find((e) => e.event_code === EVENTO_APROVADO);
          const nok = saidasDoNode.find((e) => e.event_code === EVENTO_REPROVADO);
          caminhoAprovado = ok ? nomeDoNode(ok.to_node_id) : null;
          caminhoReprovado = nok ? nomeDoNode(nok.to_node_id) : null;
          // v4.2.0: não exibimos mais aviso interno no cartão para caminhos
          // ausentes. A validação de publicação já sinaliza pendências reais.
        }

        // v3.5.0: regras de filtro exibidas em fichas dentro do próprio bloco.
        // v4.0.0: o bloco de Condição não mostra mais regra própria — a regra
        // agora vive em cada saída (é o que o motor lê).
        const filtros = n.node_type === 'inicio' ? linhasDoResumo(n.config?.trigger_filters) : [];

        return {
          id: n.id,
          type: tipoDeComponente(forma),
          position: {
            x: n.position_x ?? auto?.x ?? 40,
            y: n.position_y ?? auto?.y ?? 40,
          },
          selected: n.id === selectedNodeId,
          data: {
            label: n.label,
            shape: forma,
            resumo,
            saidas,
            alerta,
            podeEditar,
            passo: indice + 1,
            semSaida: n.node_type !== 'fim' && saidas === 0,
            onAdicionar: onAppendAfterNode,
            actions: [...(n.on_enter_actions ?? []), ...(n.on_complete_actions ?? [])],
            actionLabels,
            // v5.0.0: saídas do gateway (usadas só para posicionar as âncoras)
            saidasLista,
            onAdicionarSaida,
            // v3.6.0: desfechos da tarefa humana de aprovação
            aprovacao,
            caminhoAprovado,
            caminhoReprovado,
            // v3.5.0: fichas de regras exibidas no bloco
            filtros,

          },
        } as Node;
      }),

    [
      nodes,
      edges,
      selectedNodeId,
      actionLabels,
      eventLabels,
      layoutAutomatico,
      podeEditar,
      onAppendAfterNode,
      onAdicionarSaida,
      onRemoverSaida,
    ],

  );

  /**
   * v4.0.0: gateways (menos a junção) têm uma âncora por saída, cujo id é o
   * próprio id da aresta. Assim cada linha sai do seu lugar no cartão.
   */
  const gateways = useMemo(
    () =>
      new Set(
        nodes
          .filter((n) => {
            const f = formaDoRegistro(n);
            return f.startsWith('gateway_') && f !== 'gateway_juncao';
          })
          .map((n) => n.id),
      ),
    [nodes],
  );

  /** v3.6.0: tarefas humanas de aprovação (âncoras "aprovado"/"reprovado") */
  const aprovacoes = useMemo(
    () =>
      new Set(
        nodes
          .filter(
            (n) =>
              n.node_type === 'tarefa_humana' &&
              String(n.config?.human_kind ?? 'aprovacao') === 'aprovacao',
          )
          .map((n) => n.id),
      ),
    [nodes],
  );

  const rfEdges = useMemo<Edge[]>(
    () =>
      edges.map((e) => ({
        id: e.id,
        source: e.from_node_id,
        target: e.to_node_id,
        // Ancora a linha na saída correta (gateway: pela aresta; aprovação: pelo evento)
        sourceHandle: gateways.has(e.from_node_id)
          ? e.id
          : aprovacoes.has(e.from_node_id)
            ? e.event_code === EVENTO_REPROVADO
              ? 'reprovado'
              : 'aprovado'
            : undefined,

        type: 'bpmnInsert',

        selected: e.id === selectedEdgeId,
        data: {
          // v3.4.0: sem rótulo escrito, mostra o evento que libera a linha
          rotulo:
            e.label ||
            (e.event_code ? (eventLabels[e.event_code] ?? e.event_code) : null) ||
            (e.is_default ? 'senão' : null),
          regressao: !!e.is_regression,
          podeEditar,
          onInserir: onInsertOnEdge,
        },
        markerEnd: { type: MarkerType.ArrowClosed },
      })),
    [edges, selectedEdgeId, podeEditar, onInsertOnEdge, gateways, aprovacoes, eventLabels],
  );



  const [flowNodes, setFlowNodes, onNodesChange] = useNodesState<Node>(rfNodes);
  const [flowEdges, setFlowEdges, onEdgesChange] = useEdgesState<Edge>(rfEdges);

  /**
   * Última posição conhecida vinda do servidor, por bloco. Serve para decidir
   * se o bloco deve voltar para a posição salva (ela mudou de verdade) ou
   * manter a posição em que o usuário acabou de soltá-lo. Sem isso qualquer
   * re-render (seleção, refetch) desfazia o arraste.
   */
  const posicoesDoServidor = useRef<Map<string, string>>(new Map());

  useEffect(() => {
    setFlowNodes((atuais) => {
      const porId = new Map(atuais.map((n) => [n.id, n]));

      return rfNodes.map((n) => {
        const chave = `${n.position.x}:${n.position.y}`;
        const anterior = posicoesDoServidor.current.get(n.id);
        const local = porId.get(n.id);
        posicoesDoServidor.current.set(n.id, chave);

        // Mesma posição no servidor → preserva o que está na tela (arraste)
        const position = local && anterior === chave ? local.position : n.position;
        return { ...n, position };
      });
    });
  }, [rfNodes, setFlowNodes]);

  useEffect(() => setFlowEdges(rfEdges), [rfEdges, setFlowEdges]);

  /**
   * v3.4.0 - "Organizar": aplica na hora as posições calculadas pelo dagre e
   * devolve o resultado para a página gravar. Antes o botão só gravava, e como
   * o canvas preserva a posição local do arraste, nada mudava na tela.
   */
  const primeiroSinal = useRef(true);
  useEffect(() => {
    if (primeiroSinal.current) {
      primeiroSinal.current = false;
      return;
    }
    const posicoes = new Map<string, { x: number; y: number }>();
    calcularLayoutBpmn(nodes, edges).forEach((p) => posicoes.set(p.id, { x: p.x, y: p.y }));
    if (posicoes.size === 0) return;

    setFlowNodes((atuais) =>
      atuais.map((n) => {
        const nova = posicoes.get(n.id);
        if (!nova) return n;
        posicoesDoServidor.current.set(n.id, `${nova.x}:${nova.y}`);
        return { ...n, position: nova };
      }),
    );
    onMoveEnd(Array.from(posicoes.entries()).map(([id, p]) => ({ id, x: p.x, y: p.y })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizarSinal]);


  /** Ao soltar o bloco, persiste todas as posições atuais */
  const handleNodeDragStop = useCallback(() => {
    onMoveEnd(flowNodes.map((n) => ({ id: n.id, x: Math.round(n.position.x), y: Math.round(n.position.y) })));
  }, [flowNodes, onMoveEnd]);

  const handleConnect = useCallback(
    (connection: Connection) => {
      if (connection.source && connection.target)
        onConnect(connection.source, connection.target, connection.sourceHandle);
    },
    [onConnect],

  );

  /**
   * Modo do canvas: "mover" arrasta a tela com o botão esquerdo (padrão atual);
   * "selecionar" permite laçar vários blocos e movê-los de uma vez.
   */
  const [modo, setModo] = useState<'mover' | 'selecionar'>('mover');
  const selecionando = modo === 'selecionar';

  const { zoomIn, zoomOut, fitView } = useReactFlow();

  /**
   * Tecla Delete: o React Flow removeria apenas da tela e o próximo carregamento
   * traria tudo de volta. Aqui delegamos para as RPCs de exclusão do editor.
   */
  const handleNodesDelete = useCallback(
    (removidos: Node[]) => {
      if (!podeEditar) return;
      removidos.forEach((n) => onDeleteNode?.(n.id));
    },
    [podeEditar, onDeleteNode],
  );

  const handleEdgesDelete = useCallback(
    (removidas: Edge[]) => {
      if (!podeEditar) return;
      removidas.forEach((e) => onDeleteEdge?.(e.id));
    },
    [podeEditar, onDeleteEdge],
  );

  return (
    <ReactFlow
      nodes={flowNodes}
      edges={flowEdges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onNodeDragStop={handleNodeDragStop}
      onConnect={handleConnect}
      onNodeClick={(_, node) => onSelectNode(node.id)}
      onEdgeClick={(_, edge) => onSelectEdge(edge.id)}
      onPaneClick={() => {
        onSelectNode(null);
        onSelectEdge(null);
      }}
      nodesDraggable={podeEditar}
      onNodesDelete={handleNodesDelete}
      onEdgesDelete={handleEdgesDelete}
      deleteKeyCode={podeEditar ? ['Delete', 'Backspace'] : null}
      /* Seleção múltipla: laço com o botão esquerdo e Shift para somar blocos */
      selectionOnDrag={selecionando}
      panOnDrag={selecionando ? [1, 2] : true}
      selectionMode={SelectionMode.Partial}
      multiSelectionKeyCode="Shift"
      fitView
      fitViewOptions={{ padding: 0.25 }}
      minZoom={0.2}
      proOptions={{ hideAttribution: true }}
      /* v3.4.0: fundo cinza (token semântico) para os cartões brancos saltarem */
      className="bg-muted"
    >
      <Background gap={18} className="!bg-muted" color="hsl(var(--muted-foreground) / 0.35)" />

      {/* Mini mapa à esquerda para não ficar atrás do painel de configuração */}
      <MiniMap pannable zoomable position="bottom-left" className="!bg-muted" />

      {/* Barra única e vertical: modo de interação + zoom (padrão Monday) */}
      {/* v3.5.0: posicionada à esquerda para ficar visível mesmo com o Sheet de propriedades aberto */}
      <Panel
        position="top-left"
        className="m-3 flex flex-col items-center gap-1 rounded-2xl border bg-card p-1 shadow-md"
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant={selecionando ? 'ghost' : 'secondary'}
              className="h-8 w-8"
              onClick={() => setModo('mover')}
              aria-label="Mover tela"
            >
              <Hand className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Mover tela</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant={selecionando ? 'secondary' : 'ghost'}
              className="h-8 w-8"
              onClick={() => setModo('selecionar')}
              aria-label="Selecionar blocos"
            >
              <MousePointer2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Selecionar blocos — segure Shift para somar</TooltipContent>
        </Tooltip>

        <span className="my-0.5 h-px w-6 bg-border" />

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => zoomIn()} aria-label="Aproximar">
              <Plus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Aproximar</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => zoomOut()} aria-label="Afastar">
              <Minus className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Afastar</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="icon"
              variant="ghost"
              className="h-8 w-8"
              onClick={() => fitView({ padding: 0.25 })}
              aria-label="Ajustar à tela"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">Ajustar à tela</TooltipContent>
        </Tooltip>
      </Panel>
    </ReactFlow>
  );
}

